import {
  REALTIME_PRESENCE_URL,
  useRealtimePresence,
  useRealtimeSubscription,
} from '@klareospace/pharos-lib-front-utils';
import type {
  Kpi,
  KpiComputedPayload,
  KpiGranularity,
  KpiKey,
  KpiPeriodBound,
  PharosRealtimeNotification,
} from '@klareospace/pharos-lib-types';
import { useQueryClient } from '@tanstack/react-query';

import type { OrderKpisTodayScope } from './use-order-kpis-today';

// ─── Constants ──────────────────────────────────────────────────────────────

const MESSENGER_API_CLIENT = 'messenger';
const KPIS_PRESENCE_PAGE = 'home';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseKpiRealtimeSyncParams {
  /** `day` is omitted for `mtd`-granularity queries — only year/month are matched then. */
  bound: KpiPeriodBound;
  enabled: boolean;
  granularity: KpiGranularity;
  kpiKey: KpiKey;
  queryKey: readonly unknown[];
  scope: OrderKpisTodayScope;
  /** Only one "today" KPI query per page should signal presence on "home" — see useOrderKpisTodayRealtimeSync. */
  signalPresence: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Shared realtime-sync core for every KPI hook (today's day-granularity queries and the "This
 * month" tab's mtd queries) — matches a `kpis:updated` notification to the current query identity
 * (`kpiKey`/`granularity`/period/scope) and patches its cache in place. Presence is always
 * requested via `useRealtimePresence` (safe to call disabled) so callers that must not signal
 * presence simply pass `signalPresence: false`.
 */
export function useKpiRealtimeSync({
  bound,
  enabled,
  granularity,
  kpiKey,
  queryKey,
  scope,
  signalPresence,
}: UseKpiRealtimeSyncParams): void {
  const queryClient = useQueryClient();

  function handleKpiComputed(notification: PharosRealtimeNotification<KpiComputedPayload>): void {
    if (notification.type !== 'resource') return;
    if (!matchesCurrentKpiQuery(notification.payload, kpiKey, granularity, bound, scope)) return;

    const { metrics, to } = notification.payload;
    queryClient.setQueryData<Kpi>(queryKey, (current) =>
      current ? { ...current, computedAt: new Date(), metrics, to } : current,
    );
  }

  useRealtimeSubscription<KpiComputedPayload>({
    enabled,
    handler: handleKpiComputed,
    operation: 'updated',
    resource: 'kpis',
  });

  useRealtimePresence({
    apiClientName: MESSENGER_API_CLIENT,
    enabled: enabled && signalPresence,
    page: KPIS_PRESENCE_PAGE,
    presenceUrl: REALTIME_PRESENCE_URL,
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** True when a `kpis:updated` payload targets the exact KPI identity the current query is showing. */
function matchesCurrentKpiQuery(
  payload: KpiComputedPayload,
  kpiKey: KpiKey,
  granularity: KpiGranularity,
  bound: KpiPeriodBound,
  scope: OrderKpisTodayScope,
): boolean {
  return (
    payload.key === kpiKey &&
    payload.granularity === granularity &&
    payload.from.year === bound.year &&
    payload.from.month === bound.month &&
    (bound.day === undefined || payload.from.day === bound.day) &&
    payload.siteId === scope.siteId &&
    payload.spaceId === scope.spaceId
  );
}
